import {
  Component,
  inject,
  signal,
  computed,
  OnInit,
  ElementRef,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { CdkDrag, CdkDragHandle, CdkDragEnd } from "@angular/cdk/drag-drop";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { TipificationDrawerComponent } from "@features/tipification/components/tipification-drawer/tipification-drawer.component";
import { TipificationState } from "../../tipification.state";
import { SettingsService } from "@core/services/settings.service";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";
import { TipificationDraftManager } from "@core/utils/tipification-draft.util";

@Component({
  selector: "app-floating-badge",
  standalone: true,
  imports: [CommonModule, CdkDrag, CdkDragHandle],
  templateUrl: "./floating-badge.component.html",
  styleUrls: ["./floating-badge.component.scss"],
})
export class FloatingBadgeComponent implements OnInit {
  @ViewChild(CdkDrag, { static: false }) cdkDrag!: CdkDrag;
  private dialog = inject(MatDialog);
  private readonly state = inject(TipificationState);
  private readonly settingsService = inject(SettingsService);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly draftManager = inject(TipificationDraftManager);
  readonly isDrawerOpen = signal(false);
  private dialogRef: MatDialogRef<TipificationDrawerComponent> | null = null;

  private readonly POSITION_KEY = "FLOATING_BADGE_POSITION";

  private isDragging = false;
  private dragTimeout: any;
  savedPosition = signal<{ x: number; y: number }>({ x: 0, y: 0 });

  readonly badgeState = computed(() => {
    const currentContext = {
      conversationId: this.tenantUrlUtil.conversationId(),
      participantIdCustomer: this.tenantUrlUtil.participantIdCustomer(),
      participantIdAgent: this.tenantUrlUtil.participantIdAgent(),
      typeInteraction: this.tenantUrlUtil.typeInteraction(),
      identityDocument: this.tenantUrlUtil.identityDocument(),
      queueId: this.tenantUrlUtil.queueId(),
    };

    return this.draftManager.getStatus(currentContext)();
  });

  ngOnInit(): void {
    // Restore saved position from localStorage
    const savedPos = this.settingsService.get(this.POSITION_KEY);
    if (savedPos && savedPos.x !== undefined && savedPos.y !== undefined) {
      this.savedPosition.set(savedPos);
    }
  }

  onDragStarted(): void {
    this.isDragging = true;
    // Clear any existing timeout
    if (this.dragTimeout) {
      clearTimeout(this.dragTimeout);
    }
  }

  onDragEnded(event: CdkDragEnd): void {
    // Save the position
    const position = event.source.getFreeDragPosition();
    this.savedPosition.set(position);
    this.settingsService.set(this.POSITION_KEY, position);

    // Keep the dragging flag true for a short time to prevent click
    this.dragTimeout = setTimeout(() => {
      this.isDragging = false;
    }, 100);
  }

  openDrawer(): void {
    // Prevent opening if dragging or multiple dialogs
    if (this.isDragging || this.isDrawerOpen() || this.dialogRef) {
      return;
    }

    // Don't reset state - we want to preserve success state

    this.isDrawerOpen.set(true);
    this.dialogRef = this.dialog.open(TipificationDrawerComponent, {
      position: { right: "0" },
      height: "100%",
      width: "450px",
      maxWidth: "90vw",
      panelClass: "tipification-drawer-container",
      hasBackdrop: true,
      backdropClass: "tipification-drawer-backdrop",
      disableClose: false,
    });

    this.dialogRef.afterClosed().subscribe((result) => {
      // Don't reset mutation state on close - keep success state
      this.isDrawerOpen.set(false);
      this.dialogRef = null;
    });
  }
}
